import { useCallback, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import { shareCoupon } from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { shareGiftDelivery } from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { giftOrderForStoreDataAtomFamily } from '@shared/api/giftOrder/giftOrder.atom';
import {
  GiftOrderDeliveryMode,
  GiftOrderForStore,
  GiftOrderItem,
  GiftOrderItemType,
  GiftStoreRole,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { simpleOpenAtomFamily } from '@shared/state/atom';

import { SHARE_GUIDE_DISMISSED_KEY } from '../utils/giftOrderCoupon.utils';

interface UseGiftOrderShareParams {
  orderId: string;
  order: GiftOrderForStore | null;
  role: GiftStoreRole;
}

export function useGiftOrderShare({ orderId, order, role }: UseGiftOrderShareParams) {
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId));

  // === 쿠폰 목록 (취소된 쿠폰 제외) ===
  const couponList = useMemo(() => {
    return ((order?.coupons ?? []) as GiftCouponForStore[]).filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    );
  }, [order]);

  // === 배송형 상품 ===
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  const deliveryMode = order?.deliveryMode;
  const isIndividualDelivery = deliveryMode === GiftOrderDeliveryMode.Individual;
  const isBulkDelivery = deliveryMode === GiftOrderDeliveryMode.Bulk;

  const deliveryList = useMemo(() => {
    return ((order?.deliveries ?? []) as GiftDeliveryForStore[]).filter(
      (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
    );
  }, [order]);

  // === 공유 가이드 모달 ===
  const [showShareGuide, setShowShareGuide] = useState(false);
  const pendingShareRef = useRef<{
    type: 'coupon' | 'delivery';
    item: GiftCouponForStore | GiftDeliveryForStore;
  } | null>(null);

  // === 쿠폰 공유 모달 ===
  const couponShareModalOpenId = `gift-coupon-share-${orderId}`;
  const setCouponShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(couponShareModalOpenId),
  );
  const [currentShareCoupon, setCurrentShareCoupon] = useState<GiftCouponForStore | null>(
    null,
  );

  const currentCouponShareUrl = useMemo(() => {
    if (!currentShareCoupon) return '';
    return `${window.location.origin}/gift-store/coupon/${currentShareCoupon.code}`;
  }, [currentShareCoupon]);

  const handleOpenCouponShareModal = useCallback(
    (coupon: GiftCouponForStore) => {
      setCurrentShareCoupon(coupon);
      setCouponShareModalOpen(true);
    },
    [setCouponShareModalOpen],
  );

  const handleCouponShareComplete = useCallback(async () => {
    if (!currentShareCoupon) return;

    try {
      await shareCoupon(currentShareCoupon._id, role);
      setOrderData((prev) => {
        if (!prev || !prev.coupons) return prev;
        return {
          ...prev,
          coupons: prev.coupons.map((c) =>
            c._id === currentShareCoupon._id
              ? { ...c, shareStatus: GiftCouponShareStatus.Shared }
              : c,
          ),
        };
      });
    } catch (error) {
      console.error('공유 상태 업데이트 실패:', error);
    }
  }, [currentShareCoupon, role, setOrderData]);

  const handleCouponShare = useCallback(
    (coupon: GiftCouponForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenCouponShareModal(coupon);
      } else {
        pendingShareRef.current = { type: 'coupon', item: coupon };
        setShowShareGuide(true);
      }
    },
    [handleOpenCouponShareModal],
  );

  // === 배송 공유 모달 (Individual) ===
  const deliveryShareModalOpenId = `gift-delivery-share-${orderId}`;
  const setDeliveryShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(deliveryShareModalOpenId),
  );
  const [currentShareDelivery, setCurrentShareDelivery] =
    useState<GiftDeliveryForStore | null>(null);

  const currentDeliveryShareUrl = useMemo(() => {
    if (!currentShareDelivery) return '';
    return `${window.location.origin}/gift-store/delivery/${currentShareDelivery.code}`;
  }, [currentShareDelivery]);

  const handleOpenDeliveryShareModal = useCallback(
    (delivery: GiftDeliveryForStore) => {
      setCurrentShareDelivery(delivery);
      setDeliveryShareModalOpen(true);
    },
    [setDeliveryShareModalOpen],
  );

  const handleDeliveryShareComplete = useCallback(async () => {
    if (!currentShareDelivery) return;

    try {
      await shareGiftDelivery(currentShareDelivery._id, role);
      setOrderData((prev) => {
        if (!prev || !prev.deliveries) return prev;
        return {
          ...prev,
          deliveries: prev.deliveries.map((d) =>
            d._id === currentShareDelivery._id
              ? { ...d, shareStatus: GiftDeliveryShareStatus.Shared }
              : d,
          ),
        };
      });
    } catch (error) {
      console.error('배송 공유 상태 업데이트 실패:', error);
    }
  }, [currentShareDelivery, role, setOrderData]);

  const handleDeliveryShare = useCallback(
    (delivery: GiftDeliveryForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenDeliveryShareModal(delivery);
      } else {
        pendingShareRef.current = { type: 'delivery', item: delivery };
        setShowShareGuide(true);
      }
    },
    [handleOpenDeliveryShareModal],
  );

  // === 하단 버튼 (쿠폰 + 배송 통합) ===
  const handleBottomButtonClick = useCallback(() => {
    const firstUnsharedCoupon = couponList.find(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    );
    if (firstUnsharedCoupon) {
      handleCouponShare(firstUnsharedCoupon);
      return;
    }

    if (isIndividualDelivery) {
      const firstUnsharedDelivery = deliveryList.find(
        (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
      );
      if (firstUnsharedDelivery) {
        handleDeliveryShare(firstUnsharedDelivery);
      }
    }
  }, [
    couponList,
    deliveryList,
    isIndividualDelivery,
    handleCouponShare,
    handleDeliveryShare,
  ]);

  const handleShareGuideConfirm = useCallback(() => {
    setShowShareGuide(false);
    if (pendingShareRef.current) {
      if (pendingShareRef.current.type === 'coupon') {
        handleOpenCouponShareModal(pendingShareRef.current.item as GiftCouponForStore);
      } else {
        handleOpenDeliveryShareModal(
          pendingShareRef.current.item as GiftDeliveryForStore,
        );
      }
      pendingShareRef.current = null;
    }
  }, [handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // === 통계 ===
  const totalCoupons = couponList.length;
  const sharedCoupons = couponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  const totalIndividualDeliveries = isIndividualDelivery ? deliveryList.length : 0;
  const sharedDeliveries = isIndividualDelivery
    ? deliveryList.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared)
        .length
    : 0;
  const unsharedDeliveries = totalIndividualDeliveries - sharedDeliveries;

  const hasShareableItems = couponList.length > 0 || isIndividualDelivery;
  const totalUnshared = unsharedCoupons + unsharedDeliveries;

  return {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,

    couponShareModalOpenId,
    currentCouponShareUrl,
    handleCouponShare,
    handleCouponShareComplete,

    deliveryShareModalOpenId,
    currentDeliveryShareUrl,
    handleDeliveryShare,
    handleDeliveryShareComplete,

    showShareGuide,
    setShowShareGuide,
    handleBottomButtonClick,
    handleShareGuideConfirm,

    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  };
}
